import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuestionnaires, useAttempts } from '@/Recoil/Topik';
import type { QuestionnaireType, AttemptType } from '@/Data/Topik/types';
import './index.scss';
import { navigateTo } from '@/Utils/routing';
import {CheckCircle} from '@mui/icons-material';
import { useSession } from '@/Recoil/Session/Session';
import { tLoader } from '@/Utils/i18n';
import { languageManager } from '@/Utils/languageManager';
import RoleSection from './Components/RoleSection';
import { useRoles } from '@/Recoil/Topik/Roles';

const ROLE_ORDER: Record<string, number> = {
    admin:    4,
    manager:  3,
    lecturer: 1,
    student:  1,
};

const translations = {
    en: {
        "TOPIK Practice Tests": "TOPIK Practice Tests",
        "Prepare for your TOPIK exam with our practice questionnaires": "Prepare for your TOPIK exam with our practice questionnaires",
        "No Questionnaires Available": "No Questionnaires Available",
        "You don't have any questionnaires allocated yet.": "You don't have any questionnaires allocated yet.",
        "Please contact support to get access to practice tests.": "Please contact support to get access to practice tests.",
        "Get TOPIK Practice Tests": "Get TOPIK Practice Tests",
        "TOPIK II Test": "TOPIK II Test",
        "Loading questionnaires...": "Loading questionnaires...",
        "Error": "Error",
        "Failed to create attempt. Please try again.": "Failed to create attempt. Please try again.",
        "Error starting test. Please try again.": "Error starting test. Please try again."
    },
    id: {
        "TOPIK Practice Tests": "Tes Latihan TOPIK",
        "Prepare for your TOPIK exam with our practice questionnaires": "Persiapkan ujian TOPIK Anda dengan kuesioner latihan kami",
        "No Questionnaires Available": "Tidak Ada Kuesioner Tersedia",
        "You don't have any questionnaires allocated yet.": "Anda belum memiliki kuesioner yang dialokasikan.",
        "Please contact support to get access to practice tests.": "Silakan hubungi dukungan untuk mendapatkan akses ke tes latihan.",
        "Get TOPIK Practice Tests": "Dapatkan Tes Latihan TOPIK",
        "TOPIK II Test": "Tes TOPIK II",
        "Loading questionnaires...": "Memuat kuesioner...",
        "Error": "Kesalahan",
        "Failed to create attempt. Please try again.": "Gagal membuat percobaan. Silakan coba lagi.",
        "Error starting test. Please try again.": "Kesalahan memulai tes. Silakan coba lagi."
    },
    ko: {
        "TOPIK Practice Tests": "TOPIK 연습 시험",
        "Prepare for your TOPIK exam with our practice questionnaires": "연습 문제로 TOPIK 시험을 준비하세요",
        "No Questionnaires Available": "사용 가능한 문제가 없습니다",
        "You don't have any questionnaires allocated yet.": "아직 할당된 문제가 없습니다.",
        "Please contact support to get access to practice tests.": "연습 시험에 액세스하려면 고객 지원에 문의하세요.",
        "Get TOPIK Practice Tests": "TOPIK 연습 시험 받기",
        "TOPIK II Test": "TOPIK II 시험",
        "Loading questionnaires...": "문제를 불러오는 중...",
        "Error": "오류",
        "Failed to create attempt. Please try again.": "시도 생성에 실패했습니다. 다시 시도해주세요.",
        "Error starting test. Please try again.": "시험 시작 오류. 다시 시도해주세요."
    },
    vn: {
        "TOPIK Practice Tests": "Bài Kiểm Tra Thực Hành TOPIK",
        "Prepare for your TOPIK exam with our practice questionnaires": "Chuẩn bị cho kỳ thi TOPIK của bạn với các bài kiểm tra thực hành của chúng tôi",
        "No Questionnaires Available": "Không Có Bảng Câu Hỏi",
        "You don't have any questionnaires allocated yet.": "Bạn chưa có bất kỳ bảng câu hỏi nào được phân bổ.",
        "Please contact support to get access to practice tests.": "Vui lòng liên hệ hỗ trợ để được truy cập vào các bài kiểm tra thực hành.",
        "Get TOPIK Practice Tests": "Nhận Bài Kiểm Tra Thực Hành TOPIK",
        "TOPIK II Test": "Bài Kiểm Tra TOPIK II",
        "Loading questionnaires...": "Đang tải bảng câu hỏi...",
        "Error": "Lỗi",
        "Failed to create attempt. Please try again.": "Không thể tạo lần thử. Vui lòng thử lại.",
        "Error starting test. Please try again.": "Lỗi khi bắt đầu bài kiểm tra. Vui lòng thử lại."
    }
};

function TopikDashboard() {
    const { questionnaires, loading: questionnairesLoading, error: questionnairesError, syncQuestionnaires } = useQuestionnaires();
    const { attempts, loading: attemptsLoading, error: attemptsError, syncAttempts, createAttempt } = useAttempts();
    const {session} = useSession();
    const [, forceUpdate] = useState({});
    const t = tLoader(translations);

    const {syncUserRole, roles} = useRoles();
    const userRole = roles[session.email];

    useEffect(() => {
        if (session.email) {
            syncUserRole({ email: session.email });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session.email]);

    console.log({session})

    useEffect(() => {
        const unsubscribe = languageManager.subscribe(() => forceUpdate({}));
        return unsubscribe;
    }, []);

    useEffect(() => {
        // Check if user is logged in
        if (!session.email) {
            navigateTo('/login?redirectTo=/topik');
        }
    }, [session.email]);

    const init = useRef(()=>{
        syncQuestionnaires();
        syncAttempts({userid: session?.userid});
    })
    useEffect(() => {
        // because syncQuestionnaires is a function that fetches data, we want to call it only once when the component mounts
        // using useRef to avoid re-creating the function on every render
        if (session.email) {
            const debounceTimer = setTimeout(() => {
                init.current();
            }, 300);

            return () => clearTimeout(debounceTimer);
        }
    }, [session.userid, session.email]);

    // Transform data for display
    const questionnaireList = useMemo(() => Object.values(questionnaires), [questionnaires]);
    const attemptMap = useMemo(() => {
        const map: Record<string, Record<'listening' | 'reading' | 'writing', AttemptType | undefined>> = {};

        questionnaireList.forEach(q => {
            const questionnaireAttempts = Object.values(attempts).filter(a => a.examid === q.examid);
            map[q.questionnaireid] = {
                listening: questionnaireAttempts.find(a => a.section === 'listening'),
                reading: questionnaireAttempts.find(a => a.section === 'reading'),
                writing: questionnaireAttempts.find(a => a.section === 'writing')
            };
        });

        return map;
    }, [questionnaireList, attempts]);

    const loading = questionnairesLoading || attemptsLoading;
    const error = questionnairesError || attemptsError;

    const getSectionStatus = (questionnaireid: string, section: 'listening' | 'reading' | 'writing') => {
        const sectionAttempt = attemptMap[questionnaireid]?.[section];
        if (!sectionAttempt) return null;
        return sectionAttempt.status;
    };

    const handleSectionClick = async (questionnaire: QuestionnaireType, section: 'listening' | 'reading' | 'writing') => {
        try {
            const sectionAttempt = attemptMap[questionnaire.questionnaireid]?.[section];

            if (sectionAttempt && sectionAttempt.status === 'completed') {
                // View result
                navigateTo(`/topik/${questionnaire.questionnaireid}/${sectionAttempt.attemptid}/${section}/result`);
            } else if (sectionAttempt) {
                // Continue test
                navigateTo(`/topik/${questionnaire.questionnaireid}/${sectionAttempt.attemptid}/${section}`);
            } else {
                // Create new attempt for this section using Recoil
                const response = await createAttempt({
                    questionnaireid: questionnaire.questionnaireid,
                    section
                });

                if (!response || !response.attemptid) {
                    alert(t('Failed to create attempt. Please try again.'));
                    return;
                }

                navigateTo(`/topik/${questionnaire.questionnaireid}/${response.attemptid}/${section}`);
            }
        } catch (error) {
            console.error('Error handling section click:', error);
            alert(t('Error starting test. Please try again.'));
        }
    };

    if (loading) {
        return (
            <div className="TopikDashboard">
                <div className="Container">
                    <div className="LoadingView">
                        <div className="LoadingSpinner"></div>
                        <p>{t('Loading questionnaires...')}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="TopikDashboard">
                <div className="Container">
                    <div className="ErrorView">
                        <h3>{t('Error')}</h3>
                        <p>{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="TopikDashboard">
            <div className="Container">
                <header className="Header">
                    {t('MATE 한국어능력시험 어드민')}
                </header>

                <RoleSection />

                {(ROLE_ORDER[userRole?.role] ?? 0) >= ROLE_ORDER['manager'] && (
                    <div className="LinkGroup">
                        <button className="Link" onClick={() => navigateTo('/topik/roles')}>
                            {"권한 관리 →"}
                        </button>
                        <button className="Link" onClick={() => navigateTo('/topik/exam')}>
                            {"시험 관리 →"}
                        </button>
                    </div>
                )}

                {questionnaireList.length === 0 ? (
                    <div className="EmptyState">
                        <div className="EmptyContent">
                            <h3>{t('No Questionnaires Available')}</h3>
                            <p>{t("You don't have any questionnaires allocated yet.")}</p>
                            <p>{t('Please contact support to get access to practice tests.')}</p>
                            <br/>
                            <button className="StartButton" onClick={() => navigateTo('/product/topik-250622')}>
                                {t("Get TOPIK Practice Tests")}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="QuestionnaireList">
                        {questionnaireList.map((questionnaire) => (
                            <div key={questionnaire.questionnaireid} className="QuestionnaireCard">
                                <div className="CardHeader">
                                    <h3 className="QuestionnaireId">{questionnaire.title}</h3>
                                </div>
                                <div className="CardContent">
                                    {(['listening', 'reading', 'writing'] as const).map(section => {
                                        const status = getSectionStatus(questionnaire.questionnaireid, section);
                                        const isCompleted = status === 'completed';
                                        const isInProgress = status === 'active';

                                        return (
                                            <button key={section} className="SectionInfo" onClick={() => handleSectionClick(questionnaire, section)}>
                                                <CheckCircle style={{width: 24, height: 24}} color={isCompleted ? "success": isInProgress ? "warning" : "disabled"}/>
                                                <span className="SectionName">{section.charAt(0).toUpperCase() + section.slice(1)}</span>
                                                <span>{"→"}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default TopikDashboard;
